<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { useBomStore } from '@/store/BomStore';
import type { CatalogueItem } from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import formatCost from '@/helpers/CurrencyHelper';
import { useCataloguePermissions } from '@/composables/catalogue/useCataloguePermissions';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';

const { $gettext } = gettext;
const bomStore = useBomStore();
const { canEditItem, canDuplicateItem, canCreateRevision } =
  useCataloguePermissions();
const actions = inject(CatalogueItemActionsKey)!;

const props = defineProps<{
  item: CatalogueItem;
  showActions?: boolean;
}>();

const canEdit = computed(() => {
  return canEditItem(props.item);
});

// Anyone with UPDATE_CATALOGUE can duplicate any item
const canDuplicate = computed(() => {
  return canDuplicateItem();
});

const canRevision = computed(() => {
  return canCreateRevision(props.item);
});

// Show menu if user can edit, duplicate, or create revision
const showMenu = computed(() => {
  return canEdit.value || canDuplicate.value || canRevision.value;
});

const mainImage = computed(() => {
  return props.item.images?.[0];
});

const handleCardClick = () => {
  actions.viewItem(props.item);
};

const isInBom = computed(() => {
  return bomStore.isInBom(props.item.uuid);
});

const bomQuantity = computed(() => {
  return bomStore.getItemQuantity(props.item.uuid);
});

// Local quantity for instant UI updates
const localQuantity = ref(bomQuantity.value);

// Sync local quantity when bomQuantity changes
watch(bomQuantity, (newValue) => {
  localQuantity.value = newValue;
});

// Track if we're currently updating to prevent duplicate calls
const isUpdatingQuantity = ref(false);

const increaseQuantity = async (event: Event) => {
  if (isUpdatingQuantity.value) {
    return;
  }

  event.stopPropagation();
  const current = bomStore.getItemQuantity(props.item.uuid);
  const newQuantity = current + 1;

  // Update local quantity immediately for responsive UI
  localQuantity.value = newQuantity;

  isUpdatingQuantity.value = true;
  try {
    await bomStore.updateQuantity(props.item.uuid, newQuantity);
  } finally {
    isUpdatingQuantity.value = false;
  }
};

const decreaseQuantity = async (event: Event) => {
  if (isUpdatingQuantity.value) {
    return;
  }

  event.stopPropagation();
  const current = bomStore.getItemQuantity(props.item.uuid);
  const newQuantity = current - 1;

  if (newQuantity > 0) {
    // Update local quantity immediately for responsive UI
    localQuantity.value = newQuantity;

    isUpdatingQuantity.value = true;
    try {
      await bomStore.updateQuantity(props.item.uuid, newQuantity);
    } finally {
      isUpdatingQuantity.value = false;
    }
  }
};

const handleQuantityBlur = async () => {
  if (isUpdatingQuantity.value) {
    return;
  }

  const num = Number(localQuantity.value);
  if (!Number.isNaN(num) && num >= 1) {
    // Only update if quantity actually changed
    if (num !== bomQuantity.value) {
      isUpdatingQuantity.value = true;
      try {
        await bomStore.updateQuantity(props.item.uuid, num);
      } finally {
        isUpdatingQuantity.value = false;
      }
    }
  } else {
    // Reset to valid value if invalid
    localQuantity.value = bomQuantity.value;
  }
};
</script>

<template>
  <v-card class="catalogue-item-card" @click="handleCardClick">
    <!-- Three-dots menu -->
    <div v-if="showMenu" class="position-absolute title-area-menu z-index-10">
      <v-menu>
        <template v-slot:activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            icon="mdi-dots-vertical"
            variant="text"
            size="small"
            color="black"
            class="menu-button"
            @click.stop
          />
        </template>
        <v-list>
          <v-list-item v-if="canEdit" @click="actions.editItem(item)">
            <template v-slot:prepend>
              <v-icon icon="mdi-pencil" size="small" />
            </template>
            <v-list-item-title>{{ $gettext('Edit') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="actions.duplicateItem(item)">
            <template v-slot:prepend>
              <v-icon icon="mdi-content-copy" size="small" />
            </template>
            <v-list-item-title>{{ $gettext('Duplicate') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canRevision" @click="actions.createRevision(item)">
            <template v-slot:prepend>
              <v-icon icon="mdi-source-branch-plus" size="small" />
            </template>
            <v-list-item-title>{{
              $gettext('Create Revision')
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canEdit" @click="actions.deleteItem(item)">
            <template v-slot:prepend>
              <v-icon icon="mdi-delete" size="small" color="error" />
            </template>
            <v-list-item-title class="text-error">{{
              $gettext('Delete')
            }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-img
      v-if="mainImage"
      :src="mainImage"
      height="250"
      cover
      class="align-end"
    >
      <v-card-title class="text-white text-shadow title-overlay">
        {{ item.name }}
        <v-chip
          v-if="
            item.version && (item.nextVersionUuid || item.previousVersionUuid)
          "
          size="x-small"
          color="info"
          class="ml-2"
        >
          v{{ item.version }}
        </v-chip>
      </v-card-title>
    </v-img>
    <v-card-title v-else class="card-title-no-image">
      {{ item.name }}
      <v-chip
        v-if="
          item.version && (item.nextVersionUuid || item.previousVersionUuid)
        "
        size="x-small"
        color="info"
        class="ml-2"
      >
        v{{ item.version }}
      </v-chip>
    </v-card-title>

    <v-card-text class="flex-grow-1">
      <div class="d-flex flex-column gap-2">
        <div class="d-flex align-center">
          <v-icon size="small" class="me-2">{{
            item.isAssembly ? 'mdi-package-variant' : 'mdi-cube-outline'
          }}</v-icon>
          {{ item.isAssembly ? $gettext('Assembly') : $gettext('Item') }}
        </div>

        <div v-if="item.category" class="d-flex align-center">
          <v-icon size="small" class="me-2">mdi-tag</v-icon>
          {{ $gettext('Category') }}: {{ item.category }}
        </div>

        <div v-if="item.cost" class="d-flex align-center">
          <v-icon size="small" class="me-2">mdi-cash</v-icon>
          {{ $gettext('Cost') }}: {{ formatCost(item.cost, item.currency) }}
        </div>

        <div v-if="item.erpId" class="d-flex align-center">
          <v-icon size="small" class="me-2">mdi-numeric</v-icon>
          {{ $gettext('Xentral ID') }}: {{ item.erpId }}
        </div>
      </div>
    </v-card-text>

    <!-- BOM Actions -->
    <v-card-actions class="pa-4 pt-0">
      <div v-if="isInBom" class="w-100">
        <!-- Quantity controls and Remove button inline -->
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <v-btn
              variant="outlined"
              size="small"
              icon="mdi-minus"
              class="mr-2"
              :disabled="!bomStore.canEditSelectedBom"
              @click="decreaseQuantity"
              @click.stop
            />
            <v-text-field
              v-model.number="localQuantity"
              type="number"
              min="1"
              step="1"
              hide-details
              density="compact"
              style="max-width: 90px"
              :disabled="!bomStore.canEditSelectedBom"
              @blur="handleQuantityBlur"
              @keyup.enter="handleQuantityBlur"
              @click.stop
            />
            <v-btn
              variant="outlined"
              size="small"
              icon="mdi-plus"
              class="ml-2"
              :disabled="!bomStore.canEditSelectedBom"
              @click="increaseQuantity"
              @click.stop
            />
          </div>
          <v-btn
            v-if="bomStore.canEditSelectedBom"
            color="error"
            variant="outlined"
            size="small"
            icon="mdi-delete"
            class="ml-2"
            @click.stop="bomStore.removeItem(item.uuid)"
          />
        </div>
      </div>
      <v-btn
        v-else
        block
        :color="
          bomStore.hasSelectedProjectAndBom && bomStore.canEditSelectedBom
            ? 'primary'
            : 'warning'
        "
        :disabled="!bomStore.canEditSelectedBom"
        prepend-icon="mdi-plus-box"
        @click.stop="actions.addToBom(item)"
      >
        {{
          !bomStore.hasSelectedProjectAndBom
            ? $gettext('Select a BOM first')
            : !bomStore.canEditSelectedBom
              ? $gettext('BOM is read-only')
              : $gettext('Add to BOM')
        }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.catalogue-item-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  position: relative;
  min-height: 400px;
}

.catalogue-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.text-shadow {
  text-shadow: 2px 2px 4px rgba(0, 0, 1);
}

.title-overlay {
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 16px;
  padding-right: 48px;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
  min-height: 60px;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
}

.v-card-text {
  flex-grow: 1;
  overflow: hidden;
  padding: 16px;
}

.card-title-no-image {
  display: flex;
  align-items: center;
  padding: 16px;
  padding-right: 48px;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
  min-height: 60px;
  flex-wrap: wrap;
}

.flex-grow-1 {
  flex-grow: 1;
}

.position-absolute {
  position: absolute;
}

.top-0 {
  top: 0;
}

.end-0 {
  right: 0;
}

.ma-2 {
  margin: 8px;
}

.z-index-10 {
  z-index: 10;
}

.title-area-menu {
  top: 8px;
  right: 8px;
  height: 48px;
  display: flex;
  align-items: center;
}

.menu-button {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 4px;
}

.menu-button:hover {
  background-color: rgba(255, 255, 255, 1);
}
</style>
